import React, { useMemo, useRef, useState, useEffect } from "react";
import Radar from 'radar-sdk-js';
import { GOOGLE_API_KEY } from "../../constants";

type RadarSuggestion = any;
type GooglePrediction = { description: string; place_id: string };

function loadGooglePlacesLibrary(apiKey: string): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));

  const w = window as any;
  if (w.google?.maps?.places) return Promise.resolve();

  if (w.__googlePlacesScriptLoadingPromise) return w.__googlePlacesScriptLoadingPromise;

  w.__googlePlacesScriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-google-places="true"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Places script")));
      return;
    }

    const script = document.createElement("script");
    script.dataset.googlePlaces = "true";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Places script"));
    document.head.appendChild(script);
  });

  return w.__googlePlacesScriptLoadingPromise;
}

function parseGoogleGeocoderResult(result: any) {
  const components: any[] = result?.address_components || [];
  const byType = (type: string) => components.find((c) => c.types?.includes(type));

  const streetNumber = byType("street_number")?.long_name || "";
  const route = byType("route")?.long_name || "";
  const city =
    byType("locality")?.long_name ||
    byType("postal_town")?.long_name ||
    byType("sublocality")?.long_name ||
    "";
  const stateCode = byType("administrative_area_level_1")?.short_name || "";
  const countryCode = byType("country")?.short_name || "";
  const postalCode = byType("postal_code")?.long_name || "";
  const formattedAddress = result?.formatted_address || "";

  return {
    streetName: `${streetNumber} ${route}`.trim(),
    cityName: city,
    state: stateCode,
    country: countryCode,
    postalCode,
    address: formattedAddress,
  };
}

const RadarPlacesSearchInput = ({defaultAddress="", handleAddressChange, setIsAddressChanged, setIsTypedAddress, onInputChange, inputInlineStyle={}}: {
  defaultAddress?: string;
  handleAddressChange: any;
  setIsAddressChanged: any;
  setIsTypedAddress: any;
  onInputChange?: (address: string) => void;
  inputInlineStyle?: any;
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const previousInputRef = useRef<string>(defaultAddress);
  const radarAutocompleteTimeoutRef = useRef<number | null>(null);
  const radarAutocompleteSeqRef = useRef(0);
  const [stateaddress, setAddress] = useState(defaultAddress);
  const [suggestions, setSuggestions] = useState<RadarSuggestion[]>([]);
  const [googleSuggestions, setGoogleSuggestions] = useState<GooglePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mode, setMode] = useState<"radar" | "google">("radar");
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const canUseGoogleFallback = useMemo(() => Boolean(GOOGLE_API_KEY), []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsDropdownOpen(false);
        setSuggestions([]);
        setGoogleSuggestions([]);
        setNoResultsMessage("");
        setMode("radar");
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  useEffect(() => {
    Radar.getLocation()
      .then((result) => {
        setUserLocation({
          latitude: result.latitude,
          longitude: result.longitude
        });
      })
      .catch((err) => {
        console.log('Could not get location:', err);
        Radar.ipGeocode()
          .then((result) => {
            if (result.address) {
              setUserLocation({
                latitude: result.address.latitude,
                longitude: result.address.longitude
              });
            }
          })
          .catch(() => {
            console.log('Using default location');
          });
      });
  }, []);

  const handleChange = (e) => {
    const address = e.target.value;
    const previousAddress = previousInputRef.current ?? "";
    const trimmedAddress = address.trim();
    const wasGoogleMode = mode === "google";
    setIsTypedAddress(true);
    setAddress(address);
    setNoResultsMessage("");
    setMode("radar");
    setGoogleSuggestions([]);
    setIsDropdownOpen(trimmedAddress.length >= 8);
    if (onInputChange) {
      onInputChange(address);
    }

    // If the user only added/removed spaces at the start/end, don't re-trigger autocomplete.
    // Example: "123 Main St" -> " 123 Main St" or "123 Main St " should not fire.
    // Exception: if we were showing Google results, we want to immediately shift back to Radar
    // and fetch suggestions even if the change was only whitespace.
    if (!wasGoogleMode && address !== previousAddress && trimmedAddress === previousAddress.trim()) {
      previousInputRef.current = address;
      return;
    }

    previousInputRef.current = address;

    const radarQuery = trimmedAddress;
    if (radarQuery.length >= 8) {
      if (radarAutocompleteTimeoutRef.current) {
        window.clearTimeout(radarAutocompleteTimeoutRef.current);
      }

      const seq = ++radarAutocompleteSeqRef.current;
      setLoading(true);
      radarAutocompleteTimeoutRef.current = window.setTimeout(() => {
        const params:any = {
          query: radarQuery,
          limit: 10,
          countryCode: "US",
        };

        if (userLocation) {
          params.near = {
            latitude: userLocation.latitude,
            longitude: userLocation.longitude
          };
        }

        Radar.autocomplete(params)
          .then((result) => {
            if (seq !== radarAutocompleteSeqRef.current) return;
            setSuggestions(result.addresses || []);
          })
          .catch((err) => {
            if (seq !== radarAutocompleteSeqRef.current) return;
            console.error('Radar autocomplete error:', err);
            setSuggestions([]);
          })
          .finally(() => {
            if (seq !== radarAutocompleteSeqRef.current) return;
            setLoading(false);
          });
      }, 300);
    } else {
      if (radarAutocompleteTimeoutRef.current) {
        window.clearTimeout(radarAutocompleteTimeoutRef.current);
        radarAutocompleteTimeoutRef.current = null;
      }
      radarAutocompleteSeqRef.current += 1;
      setLoading(false);
      setSuggestions([]);
    }
  };

  const handleSelect = (suggestion: RadarSuggestion) => {
    setIsTypedAddress(false);
    setAddress(suggestion.formattedAddress);
    setIsAddressChanged(true);
    setSuggestions([]);
    setGoogleSuggestions([]);
    setNoResultsMessage("");
    setMode("radar");
    setIsDropdownOpen(false);

    const street = suggestion.number || "";
    const route = suggestion.street || "";
    const city = suggestion.city || "";
    const state = suggestion.stateCode || "";
    const country = suggestion.countryCode || "";
    const postalCode = suggestion.postalCode || "";

    let streetName = street + " " + route;
    let cityName = city;
    const address = suggestion.formattedAddress;

    handleAddressChange({streetName, cityName, state, country, postalCode, address});
  };

  const runGoogleFallback = async () => {
    const query = stateaddress?.trim();
    if (!query || query.length < 8) return;
    if (!canUseGoogleFallback) return;

    setLoading(true);
    setNoResultsMessage("");
    setMode("google");
    setIsDropdownOpen(true);

    try {
      await loadGooglePlacesLibrary(GOOGLE_API_KEY as string);
      const w = window as any;
      const service = new w.google.maps.places.AutocompleteService();

      const predictions: GooglePrediction[] = await new Promise((resolve, reject) => {
        service.getPlacePredictions(
          { input: query, componentRestrictions: { country: "us" } },
          (results: any[], status: string) => {
            if (status === w.google.maps.places.PlacesServiceStatus.OK) {
              resolve((results || []).map((r) => ({ description: r.description, place_id: r.place_id })));
            } else if (status === w.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([]);
            } else {
              reject(new Error(`Google Places status: ${status}`));
            }
          }
        );
      });

      setGoogleSuggestions(predictions);
      setSuggestions([]);
      if (predictions.length === 0) {
        setNoResultsMessage("We can't find this address. Please enter a valid address");
      }
    } catch (err) {
      console.error("Google Places fallback error:", err);
      setGoogleSuggestions([]);
      setNoResultsMessage("We can't find this address. Please enter a valid address");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGoogle = async (prediction: GooglePrediction) => {
    setIsDropdownOpen(false);
    setGoogleSuggestions([]);
    setSuggestions([]);
    setNoResultsMessage("");
    setLoading(true);

    try {
      const w = window as any;
      if (!w.google?.maps) {
        await loadGooglePlacesLibrary(GOOGLE_API_KEY as string);
      }

      const geocoder = new (window as any).google.maps.Geocoder();
      const result = await new Promise<any>((resolve, reject) => {
        geocoder.geocode({ placeId: prediction.place_id }, (results: any[], status: string) => {
          if (status === "OK" && results && results.length > 0) resolve(results[0]);
          else reject(new Error(`Geocoder status: ${status}`));
        });
      });

      const parsed = parseGoogleGeocoderResult(result);
      setIsTypedAddress(false);
      setIsAddressChanged(true);
      setAddress(parsed.address || prediction.description);
      handleAddressChange(parsed);
    } catch (err) {
      console.error("Google Places selection error:", err);
      setNoResultsMessage("We can't find this address. Please enter a valid address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={wrapperRef}>
      <label className="d-flex justify-content-between">
        Address
      </label>
      <input
        value={stateaddress}
        onChange={handleChange}
        placeholder="Select Address from here "
        className="form-control"
        style={inputInlineStyle}
      />
      {isDropdownOpen ? (
        <div className="autocomplete-dropdown-container" style={{ display: "flex", flexDirection: "column" }}>

          {/* Scrollable area */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && <div className="loading">Loading...</div>}
            {mode === "radar" &&
              suggestions.map((suggestion, index) => (
                <div
                  key={`radar-${index}`}
                  className="suggestion-item"
                  onClick={() => handleSelect(suggestion)}
                  style={{ padding: "10px", cursor: "pointer" }}
                >
                  <span>{suggestion.formattedAddress}</span>
                </div>
              ))}

            {mode === "google" &&
              googleSuggestions.map((prediction, index) => (
                <div
                  key={`google-${prediction.place_id}-${index}`}
                  className="suggestion-item"
                  onClick={() => handleSelectGoogle(prediction)}
                  style={{ padding: "10px", cursor: "pointer" }}
                >
                  <span>{prediction.description}</span>
                </div>
              ))}
          </div>

          {/* Sticky bottom — never scrolls away */}
          {noResultsMessage ? (
            <div
              className="suggestion-item"
              style={{
                padding: "10px",
                color: "#aa182c",
                flexShrink: 0,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              {noResultsMessage}
            </div>
          ) : null}

          {mode === "radar" && stateaddress?.trim()?.length >= 8 && canUseGoogleFallback ? (
            <div
              className="suggestion-item"
              onClick={runGoogleFallback}
              style={{
                padding: "10px",
                cursor: "pointer",
                color: "#0956A8",
                fontWeight: 600,
                flexShrink: 0,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              Can't find your address?
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default RadarPlacesSearchInput